/**
 * Pickers over this tenant's rows in the OTHER verticals (read-only on their
 * side): an NDT Inspection weld for a PF weld, a Pressure Testing test for a
 * dig repair. GET /pipeline-facility/link-candidates/… (PF_DISPATCH). A tenant
 * without that vertical gets an empty list, and the picker says so plainly.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useLinkPfNdt, useLinkPfPt, usePfNdtCandidates, usePfPtCandidates } from '../../../hooks/usePfDigs';
import { formatInstantDay } from '../../../lib/calendar-date';
import { fmtNumber } from '../../../lib/pipeline-facility';
import { pfDigRefusal } from '../../../lib/pf-digs';
import { PF_INPUT, PF_SECONDARY, PfFormError, PfLoading } from '../PfBits';

function useDebounced(v: string) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v.trim()), 250);
    return () => clearTimeout(t);
  }, [v]);
  return d;
}

export function NdtLinkPicker({ weldId, onDone }: { weldId: string; onDone: () => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const query = usePfNdtCandidates(dq, true);
  const link = useLinkPfNdt();
  const [error, setError] = useState<string | null>(null);
  const rows = query.data?.data ?? [];
  return (
    <div className="space-y-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3" data-testid="pf-ndt-picker">
      <PfFormError message={error} />
      <div className="flex gap-2">
        <input className={PF_INPUT} aria-label="Search NDT welds" placeholder="Search NDT weld or job number" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className={PF_SECONDARY} onClick={onDone}>
          Close
        </button>
      </div>
      {query.isLoading ? (
        <PfLoading label="Loading NDT welds" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-fieldiq-text">No NDT Inspection welds{dq ? ' match' : ' on this company'}.</p>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {rows.map((r) => (
            <li key={r.ndtWeldUid} className="flex items-center gap-3 py-2 text-sm">
              <span className="font-mono font-bold text-white">{r.weldNumber || '—'}</span>
              <span className="text-fieldiq-text">{r.jobNumber || '—'}</span>
              <span className="text-fieldiq-muted">{r.latestResult || 'No exam yet'}</span>
              <button
                type="button"
                className={`${PF_SECONDARY} ml-auto`}
                disabled={link.isPending}
                onClick={() => {
                  setError(null);
                  link.mutate(
                    { weldId, ndtWeldUid: r.ndtWeldUid },
                    {
                      onSuccess: () => {
                        toast.success(`Linked NDT weld ${r.weldNumber ?? ''}`.trim());
                        onDone();
                      },
                      onError: (err) => setError(pfDigRefusal(err, 'The NDT weld could not be linked.')),
                    },
                  );
                }}
              >
                Link
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PtLinkPicker({
  digRepairId,
  pressureUnit,
  timeZone,
  onDone,
}: {
  digRepairId: string;
  pressureUnit: string | null | undefined;
  timeZone: string | null | undefined;
  onDone: () => void;
}) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const query = usePfPtCandidates(dq, true);
  const link = useLinkPfPt();
  const [error, setError] = useState<string | null>(null);
  const rows = query.data?.data ?? [];
  return (
    <div className="space-y-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3" data-testid="pf-pt-picker">
      <PfFormError message={error} />
      <div className="flex gap-2">
        <input className={PF_INPUT} aria-label="Search pressure tests" placeholder="Search PT job or test type" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className={PF_SECONDARY} onClick={onDone}>
          Close
        </button>
      </div>
      {query.isLoading ? (
        <PfLoading label="Loading pressure tests" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-fieldiq-text">No pressure tests{dq ? ' match' : ' on this company'}.</p>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {rows.map((r) => (
            <li key={r.ptTestId} className="flex flex-wrap items-center gap-3 py-2 text-sm">
              <span className="font-mono font-bold text-white">{r.ptJobNumber || '—'}</span>
              <span className="text-fieldiq-text">{r.testTypeName || '—'}</span>
              <span className="font-mono text-fieldiq-text">
                {fmtNumber(r.testPressure)}
                {r.testPressure != null && pressureUnit ? ` ${pressureUnit}` : ''}
              </span>
              <span className="text-fieldiq-muted">{r.result || 'No result'} · {formatInstantDay(r.startedAt, timeZone) ?? '—'}</span>
              <button
                type="button"
                className={`${PF_SECONDARY} ml-auto`}
                disabled={link.isPending}
                onClick={() => {
                  setError(null);
                  link.mutate(
                    { pt_test_id: r.ptTestId, dig_repair_id: digRepairId },
                    {
                      onSuccess: () => {
                        toast.success('Pressure test linked');
                        onDone();
                      },
                      onError: (err) => setError(pfDigRefusal(err, 'The pressure test could not be linked.')),
                    },
                  );
                }}
              >
                Link
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
